/*
 * Copyright (c) 2023. Bolmate B.V.
 */

import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {AppContext} from "../../misc/appContext";
import ProcessStatus from "../../../models/processStatusModel";
import {deleteRatingsExport, downloadRatingExport, getRatingExportProcesses} from "../../../util/requestSender";
import {downloadRequiredFile, nullify, ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {createErrorMessage} from "../../../util/utils";
import {ActivateHandle, ProcessingStatus} from "../../../util/interfaces";
import CreateRatingExportController from "./createRatingExportController";
import RatingExports from "./ratingExports";

interface Props {
    ean?: string;
    className?: string;
}

const LOAD_TIMEOUT = 3000; // 3 seconds

export default function RatingExportsController(props: Props) {
    const {account} = useContext(AppContext).accountContext;
    const {ean} = props;
    const [exports, setExports] = useState<Array<ProcessStatus>>([]);
    const [loading, setLoading] = useState(true);
    const [showProcessInfo, setShowProcessInfo] = useState(false);
    const createRef = useRef<ActivateHandle>(null);
    const hasActiveExports = useMemo(() => {
        return !!exports.find((ratingExport: ProcessStatus) => ratingExport.status === ProcessingStatus.RUNNING);
    }, [exports]);
    const loadTimeout = useRef<number | null>(null);

    function onCreateExport() {
        createRef.current?.activate();
    }

    function onDownloadExport(process: ProcessStatus) {
        return function () {
            if (!account) return;
            if (process.status !== ProcessingStatus.FINISHED) return;
            const fileID = process.getData('file_id');
            if (!fileID) return showDialog('Het exportbestand kan niet gevonden worden.');
            setLoading(true);
            downloadRatingExport({
                account_id: account.id, fileID: process.getData('file_id'), callback: (data: ResponseData) => {
                    setLoading(false);
                    if (data.success) {
                        downloadRequiredFile(data.download_link);
                    } else
                        showDialog(createErrorMessage(data.message ?? 'Het downloaden van de export is niet gelukt.'),
                            'Downloaden mislukt');
                }
            })
        }
    }

    function onConfirmDeleteExport(process: ProcessStatus) {
        return function () {
            showDialog('Weet je zeker dat je deze export wilt verwijderen?', 'Export verwijderen',
                ['Nee', {label: 'Ja', onClick: onDeleteExport(process), primary: false}]);
        }
    }

    function onDeleteExport(process: ProcessStatus) {
        return function () {
            if (!account) return;
            setLoading(true);
            deleteRatingsExport({
                account_id: account.id, processID: process.id, callback: (data: ResponseData) => {
                    setLoading(false);
                    if (data.success) {
                        showSnackbar('De export is verwijderd');
                        setExports(exports.filter((exp: ProcessStatus) => exp.id !== process.id));
                    } else
                        showDialog(createErrorMessage(data.message ?? 'Het verwijderen van de export is niet gelukt.'),
                            'Verwijderen mislukt');
                }
            });
        }
    }

    function onExportStarted() {
        loadExports(true);
        setShowProcessInfo(true);
    }

    function onCloseProcessInfo() {
        setShowProcessInfo(false);
    }

    function loadExports(setLoadStatus: boolean = false) {
        clearLoadTimeout();
        if (!account) return;
        if (setLoadStatus)
            setLoading(true);
        getRatingExportProcesses({
            ean: nullify(ean), account_id: account.id, callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    setExports(data.process_statuses.map((procStat: any) => new ProcessStatus(procStat)));
                } else
                    showDialog(createErrorMessage(data.message ?? 'Het laden van de exports is niet gelukt.'),
                        'Laden mislukt');
            }
        })
    }

    function clearLoadTimeout() {
        if (loadTimeout.current !== null) {
            window.clearTimeout(loadTimeout.current);
            loadTimeout.current = null;
        }
    }

    useEffect(() => {
        loadExports(true);
        return function () {
            clearLoadTimeout();
        }
    }, [account]);

    useEffect(() => {
        if (hasActiveExports) loadTimeout.current = window.setTimeout(loadExports, LOAD_TIMEOUT);
        return function () {
            clearLoadTimeout();
        }
    }, [exports]);

    return (
        <>
            <RatingExports onCreateExport={onCreateExport} onDownloadExport={onDownloadExport}
                           onDeleteExport={onConfirmDeleteExport} onCloseProcessInfo={onCloseProcessInfo}
                           showProcessInfo={showProcessInfo} exports={exports} loading={loading}/>
            <CreateRatingExportController ref={createRef} ean={ean} onReload={onExportStarted}/>
        </>
    )
}